'use client';

import React from 'react';
import Link from 'next/link';
import BrandMark from './BrandMark';

interface FooterProps {
  className?: string;
}

const Footer = ({ className = '' }: FooterProps) => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = React.useState('');
  const [newsletterState, setNewsletterState] = React.useState<'idle' | 'saving' | 'done' | 'error'>('idle');

  const productLinks = [
    { name: 'All products', path: '/products' },
    { name: 'AI Chat', path: '/products/ai-chat-bot' },
    { name: 'WhatsApp', path: '/products/whatsapp-automation' },
    { name: 'CustomerGraph', path: '/products/customergraph' },
    { name: 'CRM', path: '/products/crm' },
    { name: 'Email Marketing', path: '/products/email-marketing' },
    { name: 'Support', path: '/products/support' },
  ];

  const resourceLinks = [
    { name: 'Pricing', path: '/pricing' },
    { name: 'Blog', path: '/blog' },
    { name: 'WhatsApp Business API', path: '/whatsapp-business-api' },
    { name: 'How It Works', path: '/how-it-works' },
    { name: 'Features', path: '/features' },
  ];

  const companyLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const subscribeNewsletter = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setNewsletterState('error');
      return;
    }

    const apiBaseUrl = process.env.NEXT_PUBLIC_API_BASE_URL;
    if (!apiBaseUrl) {
      setNewsletterState('error');
      return;
    }

    try {
      setNewsletterState('saving');
      const response = await fetch(`${apiBaseUrl}/public/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          sourcePage: 'website_footer',
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        }),
      });
      if (!response.ok) throw new Error('Newsletter signup failed');
      setEmail('');
      setNewsletterState('done');
    } catch {
      setNewsletterState('error');
    }
  };

  return (
    <footer className={`border-t border-slate-800 bg-slate-950 text-white ${className}`}>
      <div className="mx-auto w-[min(94vw,1520px)] px-1 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 mb-8">
          {/* Brand Section */}
          <div className="space-y-4">
            <Link href="/" className="flex items-center">
              <BrandMark textClassName="text-white" taglineClassName="text-violet-200" />
            </Link>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-violet-200">
              A product of Poize Games Private Limited
            </p>
            <p className="text-sm leading-relaxed text-slate-300">
              An intelligent business operating system where independent products share customer context, events, and memory.
            </p>
          </div>

          {/* Product Links */}
          <div>
            <h3 className="mb-4 text-sm font-heading font-semibold text-white">
              Products
            </h3>
            <ul className="space-y-3">
              {productLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    href={link.path}
                    className="text-sm text-slate-300 transition-colors duration-200 hover:text-violet-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="mb-4 text-sm font-heading font-semibold text-white">
              Resources
            </h3>
            <ul className="space-y-3">
              {resourceLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    href={link.path}
                    className="text-sm text-slate-300 transition-colors duration-200 hover:text-violet-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="mb-4 text-sm font-heading font-semibold text-white">
              Company
            </h3>
            <ul className="space-y-3">
              {companyLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    href={link.path}
                    className="text-sm text-slate-300 transition-colors duration-200 hover:text-violet-200"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Newsletter Section */}
          <div>
            <h3 className="mb-4 text-sm font-heading font-semibold text-white">
              Newsletter
            </h3>
            <p className="mb-4 text-sm text-slate-300">
              Product notes, launch updates, and practical customer automation ideas.
            </p>
            <form className="space-y-3" onSubmit={subscribeNewsletter}>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@company.com"
                className="h-11 w-full rounded-md border border-slate-700 bg-slate-900 px-3 text-sm text-white outline-none transition focus:border-violet-300"
              />
              <button
                type="submit"
                disabled={newsletterState === 'saving'}
                className="inline-flex h-11 w-full items-center justify-center rounded-md bg-[linear-gradient(135deg,#312E81,#4F46E5_55%,#6D5EF4)] px-4 text-sm font-semibold text-white shadow-subtle transition hover:brightness-110 disabled:opacity-70"
              >
                {newsletterState === 'saving' ? 'Subscribing...' : newsletterState === 'done' ? 'Subscribed' : 'Subscribe'}
              </button>
              {newsletterState === 'error' && (
                <p className="text-xs text-amber-200">Please enter a valid email and try again.</p>
              )}
            </form>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-slate-400">
              © {currentYear} Advika, a product of Poize Games Private Limited. All rights reserved.
            </p>
            <div className="flex items-center space-x-6">
              <Link
                href="/privacy"
                className="text-sm text-slate-400 transition-colors duration-200 hover:text-violet-200"
              >
                Privacy Policy
              </Link>
              <Link
                href="/terms"
                className="text-sm text-slate-400 transition-colors duration-200 hover:text-violet-200"
              >
                Terms of Service
              </Link>
              <Link
                href="/cookies"
                className="text-sm text-slate-400 transition-colors duration-200 hover:text-violet-200"
              >
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
